// UserInfoQuestionSetEditor.js
import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input,
  Nav, 
  NavItem, 
  NavLink, 
  TabContent, 
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select
import Select, { components } from 'react-select';

import Toast from "components/Common/Toast"; 

// Custom CSS
import "./UserInfoQuestionSetEditor.css";

import { Megaphone, MegaphoneOff, PencilLine, Package, PackageOpen } from 'lucide-react';

// Components
import QuestionEntriesTab from "./UserInfoQuestionSetEntriesTab";
import QuestionRewardsTab from "./UserInfoQuestionSetRewardsTab";
import QuestionPreviewTab from "./UserInfoQuestionSetPreviewTab";

// Common Components
import HorizontalLoader from 'components/Common/HorizontalLoader';

// Custom Hooks and utils
import { useEditableUserQuestionSetFields } from '../utils/useEditableUserQuestionSetFields';
import { uploadImage, fetchImage } from "utils/images/imageManager";

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";


/*----------------------
/* Main Component
/*----------------------*/

const UserInfoQuestionSetEditor = ({ t }) => {


  const navigate = useNavigate();
  const { infoQuestionSetId } = useParams();

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [infoQuestionSet, setInfoQuestionSet] = useState(null);
  const [infoQuestionSetEntries, setInfoQuestionSetEntries] = useState([]);
  const [questionSetLoading, setQuestionSetLoading] = useState(true);
  const [error, setError] = useState(null);

  const [profileImageUrl, setProfileImageUrl] = useState("");

  const [imageMap, setImageMap] = useState(new Map());

  const [isSavingStatus, setIsSavingStatus] = useState({});
  const [isSavedStatus, setIsSavedStatus] = useState({});


  const [activeTab, setActiveTab] = useState("questions");

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };


  /* ---------- Toast ---------- */
  const [toasts, setToasts] = useState([]);   // { id, msg, type, dur }
  const pushToast = (msg, type = "success", duration = 3000) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type, duration }]);
  };
  const popToast = id => setToasts(t => t.filter(x => x.id !== id));


  //--------------------------------------------------
  // Loading Info on Mount
  //--------------------------------------------------

  const fetchData = async () => {
    try {
      setQuestionSetLoading(true);
      setError(null);

      // ---- Load Set and Entries ----
      const [setResponse, questionsResponse] = await Promise.all([
        axiosInstance.get(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}`),
        axiosInstance.get(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/getInfoQuestionSetEntries`),
      ])
      console.log('questionSet Loaded:', setResponse.data);
      setInfoQuestionSet(setResponse.data);
      console.log('infoEntries in Set Loaded:', questionsResponse.data);
      const { questions, sections, textContents, imageContents } = questionsResponse.data;

      const mergedEntries = [
        ...questions.map((q) => ({ ...q, type: "question" })),
        ...sections.map((s) => ({ ...s, type: "section" })),
        ...textContents.map((t) => ({ ...t, type: "text" })),
        ...imageContents.map((i) => ({ ...i, type: "image" })),
      ];

      // Sort by `order`
      const sortedEntries = mergedEntries.sort((a, b) => a.order - b.order);
      setInfoQuestionSetEntries(sortedEntries);

      // ---- Initial Saved Status ----
      const initialSavedStatus = {};
      sortedEntries.forEach(e => {
        initialSavedStatus[e._id] = true;
      });
      setIsSavedStatus(initialSavedStatus);

      return {
        questionSet: setResponse.data,
        entries: questionsResponse.data,
        sortedEntries,
      };
    } catch (err) {
      console.error(err);
      setError("Failed to load data.");
    } finally {
      setQuestionSetLoading(false);
    }
  };

  const createdObjectUrls = useRef(new Set());
  const fetchQuestionSetImages = async (questionSet) => {
    try {

      const imageFileName = questionSet?.image ? questionSet.image : "";
      if(!imageFileName) { return; }

      const { blob } = await fetchImage(imageFileName, "original");
      const objectUrl = URL.createObjectURL(blob);
      createdObjectUrls.current.add(objectUrl);
      setProfileImageUrl(objectUrl);
    } catch (err) {
      console.error("Error fetching question set images:", err);
    }
  };

  useEffect(() => {
    (async () => {
      const { questionSet, entries, sortedEntries } = await fetchData();
      if (!questionSet) return;

      if (questionSet) {
        fetchQuestionSetImages(questionSet);
      }
    })();
  }, [infoQuestionSetId]);

  useEffect(() => {
    const loadImages = async () => {
      const imageEntries = infoQuestionSetEntries.filter(e => e.type === "image");
      const newMap = new Map(imageMap); // preserve existing

      await Promise.all(
        imageEntries.map(async (entry) => {
          const filename = entry.image;
          if (!filename || newMap.has(filename)) return;

          try {
            const { blob } = await fetchImage(filename, "original");
            const objectUrl = URL.createObjectURL(blob);
            createdObjectUrls.current.add(objectUrl);
            newMap.set(filename, objectUrl);
          } catch (e) {
            console.error("Failed to load image:", filename);
          }
        })
      );

      setImageMap(newMap);
    };

    loadImages();
  }, [infoQuestionSetEntries]);

  useEffect(()=>{
    console.log('imageMap1:', imageMap);
  },[imageMap])


  //--------------------------------------------------
  // Editing Set label
  //--------------------------------------------------

   const {
    isSaving: isSavingLabel,
    isSaved: isSavedLabel,
    isError: isSavedLabelError,
    handleChange: handleSetLabelChange,
    handleSave: handleSaveLabel
  } = useEditableUserQuestionSetFields(infoQuestionSet?.label, 'editLabel', 'label', infoQuestionSetId, axiosInstance, setInfoQuestionSet);

  const {
    isSaving: isSavingDescription,
    isSaved: isSavedDescription,
    isError: isSavedDescriptionError,
    handleChange: handleSetDescriptionChange,
    handleSave: handleSaveDescription
  } = useEditableUserQuestionSetFields(infoQuestionSet?.description, 'editDescription', 'description', infoQuestionSetId, axiosInstance, setInfoQuestionSet);


  const [isSavingProfileImage, setIsSavingProfileImage] = useState(false);
  const [isSavedProfileImage, setIsSavedProfileImage] = useState(true);
  const [isSavedProfileImageError, setIsSavedProfileImageError] = useState(null);

  const [uploadImageUrl, setUploadImageUrl] = useState("");

  const handleUploadImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    createdObjectUrls.current.add(tempUrl); // track it
    setUploadImageUrl(tempUrl);

    setIsSavingProfileImage(true);
    setIsSavedProfileImage(false);
    setIsSavedProfileImageError(null);
    try {
      const filename = await uploadImage(file); // your upload function
      const { blob } = await fetchImage(filename, "original");

      const newUrl = URL.createObjectURL(blob);
      createdObjectUrls.current.add(newUrl);
      setUploadImageUrl(newUrl);

      await axiosInstance.patch(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/updateImage`, {
        image: filename
      });

      setProfileImageUrl(prev => newUrl);
      setIsSavedProfileImage(true);

    } catch (err) {
      console.error("Upload failed:", err);
      setUploadImageUrl("");
      setIsSavedProfileImageError(err.message);
    } finally {
      setIsSavingProfileImage(false);
    }
  };


  //--------------------------------------------------
  // Publish and Unpublish
  //--------------------------------------------------

  const handlePublish = async () => {
    try {
      const response = await axiosInstance.post(
        `/knovia/myInfo/stores/${infoQuestionSet?._id}/publishCustomSet`
      );

      // Update local visibility state to 'published'
      setInfoQuestionSet(prev => ({
        ...prev,
        visibility: 'published'
      }));

      console.log("Publish successful:", response.data);
    } catch (error) {
      if (error.response?.status === 409) {
        console.log("Already published.");
        // Optionally show a toast:
        //toast.info("這個題組已經是公開狀態");
      } else {
        console.error("Failed to publish question set:", error);
      }
    }
  };


  const handleUnpublish = async () => {
    try {
      const response = await axiosInstance.post(
        `/knovia/myInfo/stores/${infoQuestionSet?._id}/unpublishCustomSet`
      );

      // Update local visibility state to 'private'
      setInfoQuestionSet(prev => ({
        ...prev,
        visibility: 'private'
      }));

      console.log("Unpublish successful:", response.data);
    } catch (error) {
      if (error.response?.status === 409) {
        console.log("Already published.");
        // Optionally show a toast:
        //toast.info("這個題組已經是公開狀態");
      } else {
        console.error("Failed to unpublish question set:", error);
      }
    }
  };

  const handleArchive = async () => {
    try {
      const response = await axiosInstance.post(
        `/knovia/myInfo/stores/${infoQuestionSet?._id}/archiveCustomSet`
      );

      setInfoQuestionSet(prev => ({
        ...prev,
        visibility: 'archived'
      }));

      console.log("Archived successfully:", response.data);
    } catch (error) {
      if (error.response?.status === 409) {
        console.log("Already archived.");
        // toast.info("這個題組已經是封存狀態");
      } else {
        console.error("Failed to archive question set:", error);
      }
    }
  };

  const handleUnarchive = async () => {
    try {
      const response = await axiosInstance.post(
        `/knovia/myInfo/stores/${infoQuestionSet?._id}/unarchiveCustomSet`
      );

      // Update local visibility state to 'private'
      setInfoQuestionSet(prev => ({
        ...prev,
        visibility: 'private'
      }));

      console.log("Unarchived successful:", response.data);
    } catch (error) {
      if (error.response?.status === 409) {
        console.log("Already unarchive.");
        // Optionally show a toast:
        //toast.info("這個題組不是封存狀態");
      } else {
        console.error("Failed to unarchive question set:", error);
      }
    }
  };

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [confirmArchiveNameInput, setConfirmArchiveNameInput] = useState("");

  const openArchiveModal = () => setShowArchiveModal(true);
  const closeArchiveModal = () => {
    setConfirmArchiveNameInput("");
    setShowArchiveModal(false);
  };

  const handleArchiveConfirm = async () => {
    if (confirmArchiveNameInput !== infoQuestionSet?.label) return;

    await handleArchive();
    closeArchiveModal();
  };



  //--------------------------------------------------
  // Render logic (Not in Use)
  //-------------------------------------------------- 
  /*
  if (questionSetLoading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!infoQuestionSet) return <div>No InfoQuestionSet found.</div>;
  */

  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <React.Fragment>
      <div className="page-content user-info-qs-editor">

        {/* toast stack */}
        {toasts.map(t => (
          <Toast
            key={t.id}
            message={t.msg}
            type={t.type}
            duration={t.duration}
            onClose={() => popToast(t.id)}
          />
        ))}

        <Container fluid className="user-info-qs-editor-container">
          <Card className="user-info-qs-editor-card">
            <CardBody className="user-info-qs-editor-card-body">

              <Row>
                <Col xs={12} md={4}>
                  <CardTitle className="mb-4">{t("編輯題組")}</CardTitle>
                </Col>
                <Col xs={12} md={8} 
                  className="d-flex flex-column mb-2 mb-md-0
                    flex-md-row align-items-end 
                    align-items-md-center justify-content-md-end"
                >
                  <div className="mb-1 mb-md-0">
                    <Button color="info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                      onClick={(e) => { navigate(`/myInfo/editTopic/${infoQuestionSet.topic._id}`); }}
                    >
                      <i className='bx bx-arrow-back' ></i>{` 回到主題`}
                    </Button>
                  </div>
                  {/*
                  <div>
                    <Button color="warning" style={{ marginRight: "0.3rem" }}>
                      開始測試填答
                    </Button>
                  </div>
                  */}
                </Col>
              </Row>

              {/* Follow */}
              <div className="d-flex justify-content-center" style={{ margin: "20px 0px 20px 0px", gap: "20px" }}>
                <div
                  className="d-flex flex-column align-items-center"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={
                     infoQuestionSet?.visibility === 'archived'
                      ? handleUnarchive
                      : openArchiveModal 
                  }
                >
                  {infoQuestionSet?.visibility === 'archived' ? (
                    <>
                      <PackageOpen size={32} strokeWidth={1.5} />
                      <p className="mb-0" style={{ marginTop: "1px" }}>取消封存題組</p>
                    </>
                  ) : (
                    <>
                      <Package size={32} strokeWidth={1.5} />
                      <p className="mb-0" style={{ marginTop: "1px" }}>封存題組</p>
                    </>
                  )}
                </div>
                <div
                  className="d-flex flex-column align-items-center"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={
                    infoQuestionSet?.visibility === 'published'
                      ? handleUnpublish
                      : handlePublish
                  }
                >
                  {infoQuestionSet?.visibility === 'published' ? (
                    <>
                      <MegaphoneOff size={32} strokeWidth={1.5} />
                      <p className="mb-0" style={{ marginTop: "1px" }}>取消發布題組</p>
                    </>
                  ) : (
                    <>
                      <Megaphone size={32} strokeWidth={1.5} />
                      <p className="mb-0" style={{ marginTop: "1px" }}>發布題組</p>
                    </>
                  )}
                </div>

                <div
                  className="d-flex flex-column align-items-center"
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <PencilLine size={32} strokeWidth={1.5} />
                  <p className="mb-0" style={{ marginTop: "1px" }}>測試填答</p>
                </div>
              </div>

              {/*
              <Input
                type="file"
                onChange={(e) => handleFileChange(e)}
              />
              <div style={{ marginTop: "1rem" }}>
                <img
                  src={testImageUrl || placeholderSmall}
                  alt="Uploaded Preview"
                  style={{ maxWidth: "300px", borderRadius: "8px" }}
                />
              </div>
              */}


              <Row style={{ padding: "1rem" }}>
                <Col xs={12}>

                  {/* Question Set Label */}
                  <Row className="mb-3">
                    <Col xs={12} sm={8} xl={6}>
                      <Row>

                        <Col xs={12} md={12} className="d-flex justify-content-between">
                          <h4>題組名稱:</h4>
                          <div style={{ marginTop: "3px" }}>
                            {isSavingLabel && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                            {!isSavingLabel && !isSavedLabelError &&
                              <span style={{ color: isSavedLabel ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                            }
                            {!isSavingLabel && isSavedLabelError &&
                              <span style={{ color: "red" }}>Error Saving Label!</span>
                            }
                          </div>
                        </Col>

                        <Col xs={12} md={12} className="d-flex gap-3 flex-row">
                          <Input
                            type="text"
                            value={infoQuestionSet?.label || ""}
                            onChange={handleSetLabelChange}
                          />
                          <Button color="outline-success" onClick={()=>{handleSaveLabel(infoQuestionSet?.label)}}
                            style={{ padding: "7px 8px" }}
                          >
                            <i className="bx bx-check" />
                          </Button>
                        </Col>

                      </Row>
                    </Col>

                  </Row>


                  {/* Question Set Description */}
                  <Row className="mb-3">
                    <Col xs={12} sm={8} xl={6}>
                      <Row>

                        <Col xs={12} md={12} className="d-flex justify-content-between">
                          <h4>題組描述:</h4>
                          <div style={{ marginTop: "3px" }}>
                            {isSavingDescription && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                            {!isSavingDescription && !isSavedDescriptionError &&
                              <span style={{ color: isSavedDescription ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                            }
                            {!isSavingDescription && isSavedDescriptionError &&
                              <span style={{ color: "red" }}>Error Saving Label!</span>
                            }
                          </div>
                        </Col>

                        <Col xs={12} md={12} className="d-flex gap-3 flex-row">
                          <Input
                            type="text"
                            value={infoQuestionSet?.description || ""}
                            onChange={handleSetDescriptionChange}
                          />
                          <Button color="outline-success" onClick={()=>{handleSaveDescription(infoQuestionSet?.description)}}
                            style={{ padding: "7px 8px" }}
                          >
                            <i className="bx bx-check" />
                          </Button>
                        </Col>

                      </Row>
                    </Col>

                  </Row>

                  {/* Question Set Image */}
                  <Row className="mb-3">
                    <Col xs={12} sm={8} xl={6}>
                      <Row>

                        <Col xs={12} md={12} className="d-flex justify-content-between">
                          <h4>題組圖片:</h4>
                          <div style={{ marginTop: "3px" }}>
                            {isSavingProfileImage && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                            {!isSavingProfileImage && !isSavedProfileImageError &&
                              <span style={{ color: isSavedProfileImage ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                            }
                            {!isSavingProfileImage && isSavedProfileImageError &&
                              <span style={{ color: "red" }}>Error Saving Label!</span>
                            }
                          </div>
                        </Col>
                        
                      </Row>

                      <div>
                        <Input type="file" accept="image/*" onChange={handleUploadImageFileChange} />
                        <div style={{ marginTop: "10px" }} className="d-flex justify-content-center">
                          <img src={uploadImageUrl || profileImageUrl || placeholderSmall} alt="preview" 
                            style={{ width: '80%', borderRadius: '10px' }} 
                          />
                        </div>
                      </div>
                    </Col>

                  </Row>
  
                  <hr />

                  <Nav tabs className="mb-3">
                    <NavItem>
                      <NavLink
                        className={activeTab === "questions" ? "active" : ""}
                        onClick={() => toggleTab("questions")}
                        style={{ cursor: "pointer" }}
                      >
                        題組中的問題
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "reward" ? "active" : ""}
                        onClick={() => toggleTab("reward")}
                        style={{ cursor: "pointer" }}
                      >
                        題組獎勵
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "preview" ? "active" : ""}
                        onClick={() => toggleTab("preview")}
                        style={{ cursor: "pointer" }}
                      >
                        題組預覽
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="questions">
                      <QuestionEntriesTab
                        infoQuestionSetId={infoQuestionSetId}
                        infoQuestionSetEntries={infoQuestionSetEntries}
                        setInfoQuestionSetEntries={setInfoQuestionSetEntries}
                        isSavingStatus={isSavingStatus}
                        setIsSavingStatus={setIsSavingStatus}
                        isSavedStatus={isSavedStatus}
                        setIsSavedStatus={setIsSavedStatus}
                        imageMap={imageMap}
                        setImageMap={setImageMap}
                        navigate={navigate}
                        setError={setError}
                      />
                    </TabPane>

                    <TabPane tabId="reward">
                      <QuestionRewardsTab
                        infoQuestionSetId={infoQuestionSetId}
                        infoQuestionSet={infoQuestionSet}
                        setInfoQuestionSet={setInfoQuestionSet}
                        setError={setError}
                      />
                    </TabPane>

                    <TabPane tabId="preview">
                      <QuestionPreviewTab
                        infoQuestionSetId={infoQuestionSetId}
                        infoQuestionSet={infoQuestionSet}
                        setInfoQuestionSet={setInfoQuestionSet}
                        profileImageUrl={profileImageUrl}
                        imageMap={imageMap}
                        infoQuestionSetEntries={infoQuestionSetEntries}
                        setInfoQuestionSetEntries={setInfoQuestionSetEntries}
                        pushToast={pushToast}
                        setError={setError}
                      />
                    </TabPane>
                  </TabContent>


                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={showArchiveModal} toggle={closeArchiveModal}>
        <ModalHeader toggle={closeArchiveModal}>確認封存題組</ModalHeader>
        <ModalBody>
          <p>
            請輸入題組名稱
            <strong style={{ margin: "0 8px", display: "inline-block" }}>
              {infoQuestionSet?.label}
            </strong>
            以繼續封存：
          </p>
          <Input
            value={confirmArchiveNameInput}
            onChange={(e) => setConfirmArchiveNameInput(e.target.value)}
            placeholder="請輸入完整題組名稱"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeArchiveModal}>取消</Button>
          <Button
            color="danger"
            onClick={handleArchiveConfirm}
            disabled={confirmArchiveNameInput !== infoQuestionSet?.label}
          >
            確認封存
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  );
}

export default withTranslation()(UserInfoQuestionSetEditor);
